import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@telegram-apps/telegram-ui';
import { getPage } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';
import styles from './OperateLogModal.module.css';

type OperateLog = {
  id: number;
  title: string;
  logType: number;
  operMethod: string;
  requestMethod: string;
  operType: number;
  operName: string;
  operUrl: string;
  operIp: string;
  operLocation: string;
  status: number;
  note: string;
  createdAt: string;
};

const OPER_TYPE: Record<number, { label: string; cls: string }> = {
  0: { label: '其他', cls: 'typeOther' },
  1: { label: '新增', cls: 'typeAdd' },
  2: { label: '修改', cls: 'typeEdit' },
  3: { label: '删除', cls: 'typeDelete' },
  4: { label: '导出', cls: 'typeOther' },
  5: { label: '登录', cls: 'typeAdd' },
  6: { label: '登出', cls: 'typeOther' },
  7: { label: '余额调整', cls: 'typeEdit' },
};

const formatDateTime = (value?: string) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.replace('T', ' ').slice(0, 19);
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proxyId: number;
  proxyName: string;
};

export const OperateLogModal = ({ open, onOpenChange, proxyId, proxyName }: Props) => {
  const [logs, setLogs] = useState<OperateLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getPage<OperateLog>(
        `/admin/tel/proxy/operate/logs?proxyId=${proxyId}&limit=100`
      );
      setLogs(result.records);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [proxyId]);

  useEffect(() => {
    if (open) {
      fetchLogs();
    }
  }, [open, fetchLogs]);

  const name = proxyName || `代理 #${proxyId}`;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      className="app-modal"
      header={
        <div className={styles.banner}>
          <div className={styles.bannerTitle}>
            <span className={styles.bannerAvatar}>
              {firstChineseChar(name)}
            </span>
            <span className={styles.bannerName}>{name}</span>
          </div>
          <div className={styles.bannerSub}>操作记录，共 {total} 条</div>
          <div className={styles.bannerBars} aria-hidden="true">
            <i /><i /><i />
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>
            <span className="spinner" />
          </div>
        ) : error ? (
          <div className="error">{error}</div>
        ) : logs.length > 0 ? (
          <div className={styles.list}>
            {logs.map((log) => {
              const info = OPER_TYPE[log.operType] ?? OPER_TYPE[0];
              const failed = Number(log.status) === 1;
              return (
                <div key={log.id} className={styles.item}>
                  <div className={styles.itemTop}>
                    <span className={styles.title}>{log.title || '操作'}</span>
                    <span className={styles.time}>{formatDateTime(log.createdAt)}</span>
                  </div>
                  <div className={styles.itemBottom}>
                    <span className={styles.tags}>
                      <span className={`${styles.tag} ${styles[info.cls]}`}>{info.label}</span>
                      <span className={`${styles.status} ${failed ? styles.statusFail : styles.statusOk}`}>
                        {failed ? '异常' : '正常'}
                      </span>
                    </span>
                    <span className={styles.note}>{log.note}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className={styles.empty}>暂无操作记录</div>
        )}
      </div>
    </Modal>
  );
};
